import clsx from 'clsx';
import { ContentBlock } from '../types/doc';

const ContentBlockRenderer = ({ block }: { block: ContentBlock }) => {
  switch (block.type) {
    case 'text':
      return <p className="text-lg leading-relaxed text-slate-700 dark:text-slate-200">{block.data.value}</p>;
    case 'quote':
      return (
        <blockquote className="border-l-4 border-brand-400 bg-brand-50/40 px-6 py-4 text-xl italic text-brand-900 dark:border-brand-600 dark:bg-slate-900/60">
          {block.data.value}
        </blockquote>
      );
    case 'callout':
      return (
        <div className="rounded-2xl border border-brand-300 bg-brand-50 p-5 text-brand-900 shadow-card dark:border-brand-600/40 dark:bg-brand-900/30 dark:text-brand-50">
          {block.data.value}
        </div>
      );
    case 'image':
      return (
        <figure className="space-y-3">
          <img src={block.data.url} alt={block.data.caption || 'documentation illustration'} className="w-full rounded-2xl border border-slate-200 shadow dark:border-slate-700" />
          {block.data.caption ? (
            <figcaption className="text-center text-sm text-slate-500 dark:text-slate-400">
              {block.data.caption}
            </figcaption>
          ) : null}
        </figure>
      );
    case 'video':
      return (
        <div className="overflow-hidden rounded-2xl border border-slate-200 shadow dark:border-slate-700">
          <div className="aspect-video">
            <iframe title={block.data.caption || 'Workloob video'} src={block.data.url} className="h-full w-full border-0" allowFullScreen />
          </div>
          {block.data.caption ? (
            <p className="bg-slate-50 px-4 py-2 text-sm text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              {block.data.caption}
            </p>
          ) : null}
        </div>
      );
    case 'code':
      return (
        <pre className="overflow-x-auto rounded-xl bg-slate-900 p-4 text-sm text-emerald-200">
          <code>{block.data.value}</code>
        </pre>
      );
    case 'table':
      return (
        <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm dark:border-slate-700">
          <table className="min-w-full divide-y divide-slate-200 text-left text-sm dark:divide-slate-700">
            <thead className="bg-slate-50 dark:bg-slate-800/60">
              <tr>
                {block.data.headers.map((header) => (
                  <th key={header} className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {block.data.rows.map((row, idx) => (
                <tr key={idx}>
                  {row.map((cell, cellIdx) => (
                    <td key={cellIdx} className="px-4 py-3 text-slate-600 dark:text-slate-300">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'list':
      return block.data.ordered ? (
        <ol className="list-decimal space-y-2 pl-6 text-slate-700 dark:text-slate-200">
          {block.data.items.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ol>
      ) : (
        <ul className="list-disc space-y-2 pl-6 text-slate-700 dark:text-slate-200">
          {block.data.items.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      );
    default:
      return <div className={clsx('rounded-lg border border-dashed border-slate-200 p-4 text-sm text-slate-500')}>Unsupported block</div>;
  }
};

export default ContentBlockRenderer;

